import React from 'react';
import './App.scss';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import ResidentsPage from './pages/ResidentsPage/ResidentsPage.tsx';
import AppAccountsPage from './pages/AppAccountsPage/AppAccountsPage.tsx';
import CaregiversPage from './pages/CaregiversPage/CaregiversPage.tsx';
import HardwarePage from './pages/HardwarePage/HardwarePage.tsx';
import DocumentManagementPage from './pages/DocumentManagementPage/DocumentManagementPage.tsx';
import CareHomePage from './pages/CareHomePage/CareHomePage.tsx';
import CareHomeListPage from './pages/CareHomeListPage/CareHomeListPage.tsx';
import OrganisationPage from './pages/OrganisationPage/OrganisationPage.tsx';
import NotFoundPage from './pages/NotFoundPage.tsx';
import { ChakraProvider } from '@chakra-ui/react';
import AuthPage from './pages/AuthPage/AuthPage.tsx';
import aleaTheme from './aleaTheme.ts';
import MainLayout from './layouts/MainLayout.tsx';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n.ts';
import AddDocumentPage from './pages/DocumentManagementPage/AddDocumentPage.tsx';
import NewResidentPage from './pages/ResidentsPage/NewResidentPage.tsx';
import ResidentOverviewPage from './pages/ResidentsPage/ResidentOverviewPage.tsx';
import { BreakpointProvider } from './context/BreakpointContext.tsx';
import { AuthProvider } from './context/AuthContext.tsx';
import { ManifestProvider } from './context/ManifestContext.tsx';
import PublicRoute from './routes/PublicRoute.tsx';
import PrivateRoute from './routes/PrivateRoute.tsx';
import AnalysisPage from './pages/AnalysisPage/AnalysisPage.tsx';
import StyleGuidePage from './pages/StyleGuidePage/StyleGuidePage.tsx';
import DownloadButtonGuide from './pages/StyleGuidePage/DownloadButtonGuide.tsx';
import AleaListFilterGuide from './pages/StyleGuidePage/AleaListFilterGuide.tsx';
import AleaPaginationGuide from './pages/StyleGuidePage/AleaPaginationGuide.tsx';
import AleaButtonChoiceGuide from './pages/StyleGuidePage/AleaButtonChoiceGuide.tsx';
import LanguageManager from './components/LanguageManager/LanguageManager';

import { LanguageProvider } from './contexts/LanguageContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Single React Query client for the whole app (kept outside React tree on purpose).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Avoid background refetch noise when users switch tabs.
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent: React.FC = () => {
  return (
    <BreakpointProvider>
      <AuthProvider>
        <ManifestProvider>
          <LanguageManager />
          <Router>
            <Routes>
              <Route path="/login" element={<PublicRoute />}>
                <Route path="/login" element={<AuthPage />} />
              </Route>
              <Route path="/forgot-password/:token" element={<PublicRoute />}>
                <Route path="/forgot-password/:token" element={<AuthPage />} />
              </Route>
              <Route path="/reset-password/:token" element={<PublicRoute />}>
                <Route path="/reset-password/:token" element={<AuthPage />} />
              </Route>
              <Route path="/register/:token" element={<PublicRoute />}>
                <Route path="/register/:token" element={<AuthPage />} />
              </Route>            
              <Route path="/" element={<PrivateRoute />}>
                <Route element={<MainLayout />}>
                  <Route index element={<Navigate to="/residents" replace />} />
                  <Route path="/care-home" element={<CareHomePage />} />
                  <Route path="/care-home/:id" element={<CareHomePage />} />
                  <Route path="/care-home-list" element={<CareHomeListPage />} />
                  <Route path="/organisation" element={<OrganisationPage />} />
                  <Route path="/organisation/:id" element={<OrganisationPage />} />
                  <Route path="/residents" element={<ResidentsPage />} />
                  <Route
                    path="/residents/add"
                    element={<NewResidentPage />}
                  />
                  <Route
                    path="/residents/edit/:id"
                    element={<NewResidentPage />}
                  />
                  <Route path="/app-accounts" element={<AppAccountsPage />} />
                  <Route path="/caregivers" element={<CaregiversPage />} />
                  <Route path="/hardware" element={<HardwarePage />} />
                  <Route
                    path="/document-management"
                    element={<DocumentManagementPage />}
                  />
                  <Route
                    path="/document-management/add"
                    element={<AddDocumentPage />}
                  />
                  <Route
                    path="/residents/:id"
                    element={<ResidentOverviewPage />}
                  />
                  <Route path="/analysis" element={<AnalysisPage />} />
                  <Route path="/style-guide" element={<StyleGuidePage />} />
                  <Route path="/style-guide/download-button" element={<DownloadButtonGuide />} />
                  <Route path="/style-guide/list-filter" element={<AleaListFilterGuide />} />
                  <Route path="/style-guide/pagination" element={<AleaPaginationGuide />} />
                  <Route path="/style-guide/button-choice" element={<AleaButtonChoiceGuide />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Router>
        </ManifestProvider>
      </AuthProvider>
    </BreakpointProvider>
  );
};

const App: React.FC = () => {

  return (
    <ChakraProvider theme={aleaTheme}>
      <I18nextProvider i18n={i18n}>
        <LanguageProvider>
          {/* React Query cache + request lifecycle provider */}
          <QueryClientProvider client={queryClient}>
            <AppContent />
          </QueryClientProvider>
        </LanguageProvider>
      </I18nextProvider>
    </ChakraProvider>
  );
};

export default App;
